/**
 * Asset loading utility using LoadJS library.
 * Replaces the Angular RouteAssetLoader without Angular dependencies.
 */

import loadjs from 'loadjs';
import { angularInjectorProvider } from 'Injector';
import { getFilePathsFromManifest } from './getFilePathsFromManifest';

type GoogleWindow = typeof window & {
    google?: {
        maps?: {
            places?: unknown;
            importLibrary?: (library: string) => Promise<unknown>;
        };
    };
};

/**
 * Load CSS files using LoadJS with retry logic and error handling.
 * LoadJS handles duplicate prevention, parallel loading, and retry logic automatically.
 */
async function loadFiles(files: string[]): Promise<void> {
    const filesToLoad = files.map(file => `/${file}`);

    if (filesToLoad.length === 0) {
        return;
    }

    await loadjs(filesToLoad, {
        returnPromise: true,
        numRetries: 3,
    });
}

/**
 * Load Angular JavaScript files using ocLazyLoad to properly bootstrap Angular modules.
 * This ensures that Angular modules are registered and their .run() blocks execute.
 */
async function loadAngularFiles(files: string[]): Promise<void> {
    if (files.length === 0) {
        return;
    }
    const $ocLazyLoad = angularInjectorProvider.get<{
        load: (files: string[], opts: { serie?: boolean }) => Promise<void>;
    }>('$ocLazyLoad');
    await $ocLazyLoad.load(files, { serie: true });
}

function isGooglePlacesLoaded(win: GoogleWindow): boolean {
    return !!(win.google && win.google.maps && win.google.maps.places);
}

let googlePlacesPromise: Promise<void> | null = null;

export const loadGooglePlacesDependencies = async (): Promise<void> => {
    const injector = angularInjectorProvider.requireInjector();
    const $window = injector.get<GoogleWindow>('$window');

    if (isGooglePlacesLoaded($window)) {
        return;
    }

    // If the Maps API is already loaded (e.g. by vis.gl's APIProvider) but the
    // Places library isn't, import just the library instead of reloading the
    // entire API script — a second script load corrupts the existing map's
    // projection and triggers "included the Google Maps JavaScript API multiple
    // times" errors.
    if (typeof $window.google?.maps?.importLibrary === 'function') {
        await $window.google.maps.importLibrary('places');
        return;
    }

    if (!googlePlacesPromise) {
        const ConfigFactory = injector.get<{ getConfig: () => Promise<{ google_api_key: string }> }>('ConfigFactory');
        const ErrorLogService = injector.get<{ notify?: (error: unknown, metadata?: unknown) => void }>(
            'ErrorLogService',
        );

        googlePlacesPromise = ConfigFactory.getConfig()
            .then(async config => {
                const mapsUrl = `https://maps.googleapis.com/maps/api/js?libraries=places&key=${config.google_api_key}`;
                await loadjs(mapsUrl, {
                    returnPromise: true,
                    numRetries: 3,
                });
            })
            .catch(error => {
                if (typeof ErrorLogService?.notify === 'function') {
                    ErrorLogService.notify(error, { level: 'warning', source: 'loadGooglePlacesDependencies' });
                }
                googlePlacesPromise = null;
                throw error;
            });
    }

    try {
        await googlePlacesPromise;
    } finally {
        if (isGooglePlacesLoaded($window)) {
            googlePlacesPromise = null;
        }
    }
};

export async function loadArabicFontDependencies(): Promise<void> {
    const cssFiles = getFilePathsFromManifest('arabic-font-families', 'css');
    await loadFiles(cssFiles);
}

export async function loadGlobalDependencies(): Promise<void> {
    const injector = angularInjectorProvider.requireInjector();
    const Locale = injector.get<{ activeCode: string }>('Locale');

    if (Locale.activeCode === 'ar') {
        await loadArabicFontDependencies();
    }
}

/**
 * Load editor dependencies (CSS and JS files)
 */
export async function loadEditorDependencies(): Promise<void> {
    const angularFiles = getFilePathsFromManifest('editor', 'js');
    const cssFiles = getFilePathsFromManifest('editor', 'css');

    // Kick off loading everything but the editor.js code in parallel. Wait for mathjax to
    // load before trying to load the editor.js files to avoid an error like `MathJax.OutputJax[c.outputJax].Process is not a function`
    const promises = [loadFiles(cssFiles), loadArabicFontDependencies()];
    await loadFiles(['assets/mathjax/MathJax.js?config=TeX-AMS_CHTML-full&delayStartupUntil=configured']);
    await Promise.all(promises.concat([loadAngularFiles(angularFiles)]));
}

/**
 * Load reports dependencies (CSS and JS files)
 */
export async function loadReportsDependencies(): Promise<void> {
    const cssFiles = getFilePathsFromManifest('reports', 'css');
    const jsFiles = getFilePathsFromManifest('reports', 'js');
    await Promise.all([loadFiles(cssFiles), loadAngularFiles(jsFiles)]);

    const injector = angularInjectorProvider.requireInjector();
    const $translate = injector.get<{
        use: (lang?: string | false) => string;
        refresh: (lang?: string) => Promise<void>;
    }>('$translate');
    const currentLang = $translate.use(false);
    await $translate.refresh(currentLang);
}

/**
 * Load admin dependencies (editor + reports + admin files)
 * Replicates the Angular RouteAssetLoader.loadAdminDependencies functionality
 */
export async function loadAdminDependencies(): Promise<void> {
    const adminCssFiles = getFilePathsFromManifest('admin', 'css');
    const adminJsFiles = getFilePathsFromManifest('admin', 'js');

    await loadEditorDependencies();
    await loadReportsDependencies();
    await Promise.all([loadFiles(adminCssFiles), loadAngularFiles(adminJsFiles)]);
}

/**
 * Check if admin dependencies have already been loaded by looking for key admin CSS/JS files
 */
export function areAdminDependenciesLoaded(): boolean {
    // Check for some key admin files that should be present
    const hasAdminCSS = !!document.querySelector('link[href*="admin"]');
    const hasAdminJS = !!document.querySelector('script[src*="admin"]');

    return hasAdminCSS && hasAdminJS;
}
